import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { IChoosenFilterInterface } from 'app/library/all-users/interfaces/choosen-filter.interface';
import { AllUsersApiService } from 'app/library/all-users/services/all-users.api-service';
import { ExtendedAttribute } from 'app/library/extended-attributes/models/extended-attribute';
import { ExtendedAttributeFilter } from 'app/library/extended-attributes/models/extended-attribute-filter.model';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { takeUntil } from 'rxjs/operators';
import { AddressFilterCityRow } from './address-filter-city-row.interface';

@Component({
    selector: 'app-address-filter',
    templateUrl: './address-filter.component.html',
    styleUrls: ['./address-filter.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class AddressFilterComponent extends UnsubscribeOnDestroy implements OnInit, OnChanges {
    @Input()
    userAttribute: ExtendedAttribute;

    @Input()
    userAttributeFilter: ExtendedAttributeFilter;

    @ViewChild('citiesTableScrollHost', { read: ElementRef })
    private _citiesTableScrollHost?: ElementRef<HTMLElement>;

    @Output()
    addedToList = new EventEmitter<IChoosenFilterInterface[]>();

    dataSource: MatTableDataSource<AddressFilterCityRow>;
    displayedColumns = ['name', 'buttons'];

    list: IChoosenFilterInterface[] = [];
    searchInput = new FormControl();

    constructor(private _allUsersService: AllUsersApiService, private _translateService: TranslateService) {
        super();
    }

    ngOnInit(): void {
        this._allUsersService.filtersList$.pipe(takeUntil(this._unsubscribeAll)).subscribe((filters) => {
            this.list = filters;
            this.updateDataSource();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['userAttribute'] || changes['userAttributeFilter']) {
            this.updateDataSource();
        }
    }

    updateDataSource(): void {
        if (!this.userAttribute?.id) {
            return;
        }

        const cities = this.userAttributeFilter?.values || [];

        this.dataSource = new MatTableDataSource(
            cities.map((city) => {
                const label = city.translationKey ? this._translateService.instant(city.translationKey) : city.value;

                return {
                    value: city.value,
                    translationKey: city.translationKey,
                    label,
                    selected: this.list.some(
                        (item) =>
                            item.attributeId === this.userAttribute.id &&
                            item.entity.type === 'ExtendedAttribute' &&
                            !item.isRange &&
                            !item.textFilters?.length &&
                            item.value === city.value
                    ),
                };
            })
        );

        this._scheduleScrollSelectedCityIntoView();
    }

    private _scheduleScrollSelectedCityIntoView(): void {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const host = this._citiesTableScrollHost?.nativeElement;
                const selectedEl = host?.querySelector<HTMLElement>('[data-option-selected-row]');
                selectedEl?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
            });
        });
    }

    addToList(city: AddressFilterCityRow): void {
        this.addedToList.emit([
            ...this.list,
            {
                entity: { id: city.value, type: 'ExtendedAttribute' },
                valueToShow: `${this._translateService.instant(this.userAttribute.translationKey)}: ${city.label}`,
                attributeName: this.userAttribute.name,
                attributeId: this.userAttribute.id,
                value: city.value,
            },
        ]);
    }

    removeFromList(city: AddressFilterCityRow): void {
        this.addedToList.emit(
            this.list.filter(
                (item) =>
                    !(item.attributeId === this.userAttribute.id && item.entity.type === 'ExtendedAttribute' && !item.isRange && item.value === city.value)
            )
        );
    }
}
